import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useReducedMotion } from '../hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const sectionRef = useRef(null);
  const textRef = useRef(null);
  const visualRef = useRef(null);
  const imageRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      // Text reveals while supporting visual elements move slightly
      gsap.fromTo(
        textRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
          },
        }
      );

      gsap.fromTo(
        visualRef.current,
        { opacity: 0, x: 30 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
          },
        }
      );

      // Image parallax — subtle
      gsap.fromTo(
        imageRef.current,
        { y: -20 },
        {
          y: 20,
          ease: 'none',
          scrollTrigger: {
            trigger: visualRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.5,
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [prefersReducedMotion]);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="section-padding bg-surface dark:bg-dark-surface relative overflow-hidden"
    >
      <div className="section-container">
        {/* Editorial split layout */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left — text */}
          <div ref={textRef} className="lg:col-span-7">
            <span className="tech-label text-accent dark:text-dark-accent mb-6 block">
              ABOUT / IRDO
            </span>
            <h2 className="section-title mb-8">
              A global stage for
              <br />
              robotics excellence.
            </h2>
            <p className="section-desc mb-6">
              The International Robot Design Olympiad (IRDO) is a prestigious
              global competition that brings together the brightest minds in
              robotics and technology.
            </p>
            <p className="text-base sm:text-lg text-text-secondary dark:text-dark-text-secondary max-w-2xl leading-relaxed mb-12">
              We believe in creating a platform where creativity meets technical
              excellence, enabling participants to showcase their skills on an
              international stage.
            </p>

            {/* Key facts — restrained, not cards */}
            <div className="grid sm:grid-cols-3 gap-8 border-t border-border dark:border-dark-border pt-8">
              {[
                { value: '30+', label: 'Countries' },
                { value: '500+', label: 'Participants' },
                { value: '4', label: 'Categories' },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-3xl font-semibold text-text-primary dark:text-dark-text-primary font-mono">
                    {stat.value}
                  </div>
                  <div className="text-sm text-text-muted dark:text-dark-text-muted mt-1">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — visual element with image */}
          <div ref={visualRef} className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-card border border-border dark:border-dark-border mb-6">
              <img
                ref={imageRef}
                src="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&q=80"
                alt="Robotics competition"
                className="w-full h-64 lg:h-80 object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="tech-label text-white">IRDO / 2026</span>
              </div>
            </div>

            <div className="border border-border dark:border-dark-border rounded-card p-8 bg-surface-secondary dark:bg-dark-surface-secondary">
              <span className="tech-label text-accent dark:text-dark-accent mb-4 block">
                MISSION / 01
              </span>
              <h3 className="text-2xl font-semibold text-text-primary dark:text-dark-text-primary mb-4">
                Engineering the future
              </h3>
              <p className="text-text-secondary dark:text-dark-text-secondary leading-relaxed mb-8">
                To foster innovation in robotics and inspire the next generation
                of engineers and scientists to push the boundaries of what's
                possible with robotic technology.
              </p>

              <span className="tech-label text-accent dark:text-dark-accent mb-4 block">
                VISION / 02
              </span>
              <h3 className="text-2xl font-semibold text-text-primary dark:text-dark-text-primary mb-4">
                Accessible innovation
              </h3>
              <p className="text-text-secondary dark:text-dark-text-secondary leading-relaxed">
                A world where robotics and AI are accessible to all, driving
                progress and solving humanity's most pressing challenges through
                innovation and collaboration.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;